// config/i18n/en.js
// Complete UI string dictionary for Shift
// Every user-facing string lives here
// Never hardcode strings in components

export const strings = {
  // ── Navigation ──────────────────────────────────────────
  nav: {
    space: 'Space',
    playbook: 'Playbook',
    innovate: 'Innovate',
    digest: 'Digest',
    ideas: 'Ideas',
    notes: 'Notes',
    widgets: 'Widgets',
    signal: 'Signal',
    settings: 'Settings',
    feedback: 'Feedback',
    artifacts: 'Artifacts',
    incognito: 'Incognito',
    new_thread: 'New thread',
    search: 'Search',
    invite: 'Invite',
  },

  // ── Common actions ─────────────────────────────────────
  common: {
    close: '×',
    cancel: 'Cancel',
    save: 'Save',
    delete: 'Delete',
    edit: 'Edit',
    select: 'Select',
    done: 'Done',
    copy: 'Copy',
    pin: 'Pin',
    unpin: 'Unpin',
    share: 'Share',
    export: 'Export',
    send: 'Send',
    loading: 'loading···',
    saving: 'saving···',
    sending: 'sending···',
    generating: 'Generating...',
    search_placeholder: 'Search...',
    empty_state: 'Nothing here yet.',
    something_went_wrong: 'Something went wrong.',
    try_again: 'Try again.',
    view_grid: 'Grid view',
    view_list: 'List view',
    expand: 'Expand',
    collapse: 'Collapse',
    back: 'Back',
    next: 'Next',
    confirm: 'Confirm',
    remove: 'remove',
    download: 'Download',
    preview: 'Preview',
    code: 'Code',
  },

  // ── Chat ───────────────────────────────────────────────
  chat: {
    placeholder: 'Message...',
    placeholder_deep_search: 'What do you want to research?',
    placeholder_space: 'Say something to the room',
    placeholder_lens: "Talk to Shift about what's emerging...",
    placeholder_artifact: 'Explore this artifact with Shift...',
    something_went_wrong: 'Something went wrong. Try again.',
    file_too_large: 'The file is too large to send directly. Try a smaller image or paste the text instead.',
    no_threads: 'No threads yet.',
    leave: 'leave',
  },

  // ── Space ──────────────────────────────────────────────
  space: {
    title: 'Space',
    tagline: 'Build, learn, create — solo or with anyone.',
    sessions: 'Sessions',
    invites: 'Invites',
    no_sessions: 'No shared sessions yet. Invite someone and Shift will hold space for both of you.',
    no_invites: 'No invites yet.',
    invite_button: '+ Invite someone',
    invite_to_space: '+ Invite someone to a space',
    waiting: 'waiting for them to join',
    no_messages: 'no messages yet',
    open: 'Open →',
    resume: 'Resume →',
    accept: 'Accept →',
    type_space: 'Space',
    type_professional: 'Professional',
    type_incognito: 'Incognito',
    type_facilitated: 'Facilitated',
    pending: 'Pending',
    incognito_banner: 'Incognito — nothing leaves this room. All messages deleted when you leave.',
    incognito_leave_confirm: 'Leaving will permanently delete all messages in this incognito session. This cannot be undone. Leave?',
    raw: 'Raw',
    lens: 'Lens',
    depth: 'Depth',
  },

  // ── Artifacts ──────────────────────────────────────────
  artifacts: {
    title: 'Artifacts',
    objects: 'Objects',
    sessions_tab: 'Sessions',
    discovery: 'Discovery',
    explore: 'Explore →',
    send: 'Send',
    view: 'View',
    no_artifacts: 'No artifacts yet.',
    no_sessions: 'Open an artifact to start a session here.',
    discovery_empty: 'Discovery runs every Monday. Have more conversations this week to build your first synthesis.',
    save_to_artifacts: 'Save to Artifacts',
    type_web: 'Web',
    type_code: 'Code',
    type_document: 'Document',
    type_image: 'Image',
    type_data: 'Data',
    type_upload: 'Upload',
    collection: 'Collection',
    session: 'Session',
    core_insight: 'Core insight',
  },

  // ── Depth reports ──────────────────────────────────────
  depth: {
    no_reports: 'No depth reports yet.',
    subtitle: 'Shift generates a depth report once a week.',
    generate: 'Generate Depth Report',
    new_report: '+ New Report',
    analyzing: 'Analyzing conversation... this may take a moment.',
    report: 'Report',
    analysis: 'Analysis',
    incognito_message: 'Incognito session.\nDepth reports are not available — nothing persists after you leave.',
  },

  // ── Insight / Lens ─────────────────────────────────────
  insight: {
    title: 'Insight',
    observation: 'Observation',
    personal: 'Personal',
    empty_title_space: 'Your private lens on the conversation.',
    empty_subtitle_space: 'When Shift catches something worth exploring, it will appear here.',
    empty_title_artifact: 'Your private space with {aiName}.',
    empty_subtitle_artifact: 'When {aiName} catches something worth exploring, it will appear here.',
    toast: 'New observation in Lens',
    toast_artifact: 'New insight from {aiName}',
  },

  // ── Invite ─────────────────────────────────────────────
  invite: {
    invite_to_shift: 'Invite to Shift',
    invite_to_space: 'Invite to Space',
    their_email: 'Their email',
    space_title: 'Space title',
    reason: 'Reason for invitation',
    reason_optional: '(optional)',
    article: 'Article or material',
    article_optional: '(optional — paste text)',
    attach_file: 'Attach a file',
    attach_optional: '(optional)',
    file_types: 'PDF, Word, Excel, CSV — up to 10MB',
    send_invite: 'Send Invite',
    invite_sent: 'Invite sent.',
    new_user_note: "New to Shift? They'll receive a sign-in link and land directly in this space once they join.",
    invalid_email: 'Invalid email address',
    invalid_email_missing_at: 'Missing @ — enter a full email address',
    invalid_email_check_domain: 'Invalid email address — check the domain',
  },

  // ── Settings ───────────────────────────────────────────
  settings: {
    title: 'Settings',
    subtitle: 'account · ai · privacy',
    tab_account: 'Account',
    tab_ai: 'AI',
    tab_privacy: 'Privacy',
    tab_session: 'Session',
    tab_danger: 'Danger zone',
    ai_name: 'AI name',
    response_length: 'Response length',
    length_brief: 'Brief',
    length_balanced: 'Balanced',
    length_thorough: 'Thorough',
    depth_mode: 'Depth mode',
    depth_practical: 'Practical only',
    depth_open: 'Open to depth',
    depth_welcome: 'Depth welcome',
    language: 'Language',
    interface_language: 'Interface',
    response_language: 'Shift responds in',
    response_auto: 'Match my language',
  },

  // ── Profile ────────────────────────────────────────────
  profile: {
    portrait: 'portrait',
    progress: 'progress',
    protocols: 'protocols',
    generate_insights: 'Generate insights',
    generating_insights: 'Generating...',
    still_learning: '{aiName} is still learning about you.',
    still_learning_sub: 'Keep having conversations — your portrait builds over time.',
    layer_who: 'Who You Are',
    layer_how: 'How You Operate',
    layer_building: "What You're Building",
    layer_way: 'What Gets In Your Way',
    layer_learning: 'What Shift Is Still Learning',
    feedback_right: 'This is right',
    feedback_partial: 'Partially right',
    feedback_wrong: 'Wrong',
    confirmed: 'Confirmed',
    corrected: 'Correction noted',
    building: 'Building...',
    not_enough_data: 'Not enough data yet. Keep having conversations.',
  },

  // ── Ideas ──────────────────────────────────────────────
  ideas: {
    title: 'Ideas',
    empty: 'No ideas yet.',
    empty_sub: 'Catch it before it disappears.',
    projects: 'Projects',
    execution: 'Execution',
  },

  // ── Notes ──────────────────────────────────────────────
  notes: {
    title: 'Notes',
    inbox: 'Inbox',
    all: 'All',
    voice: 'Voice',
    no_notes: 'No notes yet.',
    no_voice: 'No voice captures yet.',
    voice_hint: "Say 'Hey {aiName}' or tap the mic to start.",
    transcribing: 'transcribing...',
    processing: 'processing...',
    in_inbox: '{count} in inbox',
  },

  // ── Playbook ───────────────────────────────────────────
  playbook: {
    title: 'Playbook',
    tagline: "Your tools. The community's wisdom.",
    lenses: 'Lenses',
    plays: 'Plays',
    reads: 'Reads',
    frames: 'Frames',
  },

  // ── Signal ─────────────────────────────────────────────
  signal: {
    title: 'Signal',
    tagline: 'What your sessions are teaching you.',
  },

  // ── Innovate ───────────────────────────────────────────
  innovate: {
    title: 'Innovate',
    tagline: 'Learn deeply. Create freely.',
  },

  // ── Digest ─────────────────────────────────────────────
  digest: {
    title: 'Digest',
    tagline: 'Read everything. Miss nothing.',
  },

  // ── Danger zone ────────────────────────────────────────
  danger: {
    delete_account: 'Delete account',
    delete_warning: 'Everything Shift knows about you will be permanently deleted. This cannot be undone.',
    what_gets_deleted: 'What gets deleted',
    items: [
      'All sessions and messages',
      'Your portrait and patterns',
      'All artifacts and versions',
      'Your playbook, notes, and signals',
      'Your account and profile',
    ],
    confirm_checkbox: 'I understand this is permanent and cannot be undone',
    delete_button: 'Delete my account',
    deleting: 'Deleting...',
    grace_period: 'You have 30 days to log back in and cancel before deletion runs.',
  },

  // ── Mobile ─────────────────────────────────────────────
  mobile: {
    disclaimer: 'Mobile Shift is built for quick touchpoints — approvals, check-ins, brief reviews.',
    disclaimer_sub: "When you're ready to go deep, desktop is where the full experience lives.",
    dismiss: 'Got it',
  },

  // ── Thread Intelligence ────────────────────────────────
  thread_intelligence: {
    title: 'Thread Intelligence',
    section_context: 'Thread Context',
    section_highlights: 'Highlights',
    section_artifacts: 'Artifacts',
    view_full: 'View full',
    pull_to_chat: 'Pull into message',
    export_label: 'Export',
    copy_text: 'Copy as text',
    copy_markdown: 'Copy as markdown',
    download_pdf: 'Download as PDF',
  },

  // ── Highlights ─────────────────────────────────────────
  highlights: {
    color_yellow: 'Important',
    color_green: 'Actionable',
    color_blue: 'Question',
    color_red: 'Contradiction',
    color_gold: 'Pin this',
    library_empty: 'No highlights yet.',
    library_empty_sub: 'Select text anywhere in Shift to highlight.',
  },

  // ── Pins ───────────────────────────────────────────────
  pins: {
    title: 'Pins',
    empty: 'Nothing pinned yet.',
    empty_sub: 'Pin notes, highlights, or ideas to give Shift standing context.',
    limit_reached: 'You have 5 active pins.',
    limit_question: 'Which one should this replace?',
  },

  // ── Voice ──────────────────────────────────────────────
  voice: {
    talk_soon: 'Talk soon.',
    voice_on: 'voice on',
    speaking: 'speaking...',
    listening: 'listening...',
  },

  // ── Onboarding ─────────────────────────────────────────
  onboarding: {
    welcome: 'Welcome to Shift',
    username_label: 'Choose a username',
    username_placeholder: 'username',
    ai_name_label: 'Name your AI companion',
    ai_name_placeholder: 'Emily, Atlas, Sage...',
    continue: 'Continue',
    username_taken: 'Username already taken',
  },

  // ── Entry screens ──────────────────────────────────────
  entry: {
    continue_button_default: 'Show me how →',
  },

  // ── Language tiers ─────────────────────────────────────
  language: {
    tier2_note: 'Available but with reduced nuance.',
    tier2_detail: 'Shift will always do its best.',
  },
};
